import React, { useEffect, useRef, useState } from 'react';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Plus, Minus, Hexagon, Users, Layout, MousePointer2, Smartphone, Layers, Globe, PenTool } from 'lucide-react';
import Logo from '../../components/Logo';
import SectionHeading from '../campaigns/components/SectionHeading';
import {
  CropMarks,
  IndexTag,
  LiveCoordTag,
  RegistrationMark,
  VerticalLabel,
  SectionFrame,
  SectionCounter,
  GrainOverlay,
  GridOverlay,
} from '../../components/swiss/SwissKit';
import { Nav, TRANSLATIONS, BookingModal, trackEvent, BRAND_LOGOS, ServiceItem, SiteFooter } from '../LandingPage';
import { StatsStrip } from '../../components/Stats';

/* --------------------------------------------------------------------------------
   CRE8NOW SEO / GEO SOLUTION PAGE — SHARED TEMPLATE

   Generic renderer for the standing 6-section structure every solution page
   (Design or AI) follows: 01 Intro · 02 About & Solves (what it is, why it
   matters, what it solves — as a before/after) · 03 Who it's for · 04 FAQ ·
   05 More solutions · 06 CTA. Brands live as a marquee inside the hero, not
   a standalone section.

   Individual pages (currently just UxDesignPage.jsx — the master
   reference — with more to follow once this template settles) own their
   own bilingual CONTENT object — copy, capabilities, FAQ, cross-links —
   and render this template. Keeps every solution page structurally
   consistent while each topic's content stays distinct.
-------------------------------------------------------------------------------- */

const MAGENTA = 'var(--cre8-magenta)';
const YELLOW = 'var(--cre8-yellow)';
const ORIGIN = 'https://cre8now.design';
const CAPABILITY_ICONS = [Hexagon, Layers, Users, MousePointer2, Layout, Smartphone, Globe, PenTool];
const TOTAL_SECTIONS = 6;

/* Primary CTA button — same mechanic as the main site's real hero CTA
   (bordered rect, no radius, a fill layer that slides up from the bottom
   on hover, the arrow expanding in alongside it), not a bespoke button
   invented for this page. Colors are the only thing that adapt: base
   stays white/ink-text (readable on the magenta/yellow section
   backgrounds these pages use), hover fills to ink with white text. */
const PRIMARY_CLASS = 'group relative inline-block px-[2.4em] py-[1.1em] font-black uppercase ui-text transition-all duration-500 overflow-hidden rounded-none border';

export const PrimaryButton = ({ onClick, children, bgColor = '#ffffff', textColor, hoverFill = 'var(--cre8-ink)', hoverTextColor = '#ffffff' }) => (
  <button
    onClick={onClick}
    style={{ backgroundColor: bgColor, borderColor: bgColor, color: textColor, '--hover-text': hoverTextColor }}
    className={PRIMARY_CLASS}
  >
    <div
      className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500"
      style={{ backgroundColor: hoverFill }}
    />
    <span className="relative z-10 flex items-center transition-colors duration-500 group-hover:text-[var(--hover-text)]">
      {children}
      <span className="w-0 opacity-0 group-hover:w-[1.25em] group-hover:ml-[0.75em] group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
        <ArrowRight size="1.1em" />
      </span>
    </span>
  </button>
);

/* Same button as a real link (external projects, live sites), with the
   same hover mechanic as the hero CTAs: the arrow slides in on hover
   (up-right, since it leaves the site). font-ui gives the <a> the button
   font the global `button` rule can't reach. */
export const PrimaryLink = ({ href, onClick, children, bgColor = '#ffffff', textColor, hoverFill = 'var(--cre8-ink)', hoverTextColor = '#ffffff', external = true }) => (
  <a
    href={href}
    onClick={onClick}
    target={external ? '_blank' : undefined}
    rel={external ? 'noopener noreferrer' : undefined}
    style={{ backgroundColor: bgColor, borderColor: bgColor, color: textColor, '--hover-text': hoverTextColor }}
    className={`${PRIMARY_CLASS} font-ui`}
  >
    <div
      className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500"
      style={{ backgroundColor: hoverFill }}
    />
    <span className="relative z-10 flex items-center transition-colors duration-500 group-hover:text-[var(--hover-text)]">
      {children}
      <span className="w-0 opacity-0 group-hover:w-[1.25em] group-hover:ml-[0.75em] group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
        <ArrowUpRight size="1.1em" />
      </span>
    </span>
  </a>
);

/* Swiss editorial FAQ row — numbered index, hairline rule, serif question,
   sans answer, plus/minus state. Native <details> so the answer stays in
   the HTML (crawlable) even collapsed. */
/* Swiss editorial FAQ row. The answer stays permanently mounted in the DOM
   (crawlable even collapsed) — only its visual height animates, via a
   plain button + aria-expanded rather than native <details>, since
   <details> snaps its content open/closed instantly with no way to
   transition that. */
const FaqRow = ({ item, idx, color }) => {
  const [open, setOpen] = useState(false);
  return (
    <Motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: Math.min(idx, 6) * 0.05 }}
      viewport={{ once: false, amount: 0.2 }}
      className="border-t border-border-color py-6 md:py-8"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-6 cursor-pointer text-left"
      >
        <div className="flex items-start gap-4 md:gap-8 flex-1">
          <span className="font-mono-swiss text-[10px] tracking-[0.2em] opacity-40 pt-1.5 flex-shrink-0 tabular-nums">{String(idx + 1).padStart(2, '0')}</span>
          <h5 className="text-lg md:text-2xl font-black tracking-tight pr-4">{item.q}</h5>
        </div>
        <Motion.span
          className="flex-shrink-0 mt-1"
          style={{ color }}
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.35, ease: [0.83, 0, 0.17, 1] }}
        >
          {open ? <Minus size={18} /> : <Plus size={18} />}
        </Motion.span>
      </button>
      <Motion.div
        initial={false}
        animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.83, 0, 0.17, 1] }}
        className="overflow-hidden"
      >
        <p className="mt-4 md:mt-5 md:ml-[52px] text-text-muted font-light leading-relaxed max-w-4xl pb-1">{item.a}</p>
      </Motion.div>
    </Motion.div>
  );
};

/* Section 02's "what it solves" list — three equal blocks per row:
   PROBLEM (grey panel) · LÖSUNG (plain centered text) · ERGEBNIS (color
   panel). Left and right match exactly — same panel, same headline
   size/weight, just grey-on-ink vs. color-on-white. */
export const SolveRow = ({ item, idx, color, fg = '#ffffff' }) => (
  <Motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: Math.min(idx, 6) * 0.05 }}
    viewport={{ once: false, amount: 0.2 }}
    className="relative grid md:grid-cols-[1fr_32px_1fr_32px_1fr] gap-8 md:gap-6 items-center py-4 px-3 md:px-5"
  >
    {/* Same corner crop marks the hero uses — one frame around the whole
        step (problem → response → outcome), not one per element. */}
    <CropMarks inset={0} color={color} className="opacity-30" />

    {/* Left (problem) and right (outcome) share the exact same shape —
        a solid color panel around a bold, centered headline, same
        padding, same size — so they read as a true pair. Center
        (solution) carries no panel, just the response as plain centered
        text, with an arrow either side connecting all three. On mobile
        the row stacks into a single column, so the arrows rotate 90° to
        point down the stack instead of being hidden outright — the flow
        stays legible at every size instead of disappearing below md. */}
    <div className="p-8 md:p-10 text-center" style={{ backgroundColor: 'var(--surface-color)' }}>
      <h5 className="font-black tracking-tight text-2xl md:text-3xl leading-tight">{item.problem}</h5>
    </div>

    <ArrowRight size={20} className="justify-self-center opacity-50 rotate-90 md:rotate-0" style={{ color }} />

    <p className="text-text-muted font-light leading-relaxed text-center px-4">{item.response}</p>

    <ArrowRight size={20} className="justify-self-center opacity-50 rotate-90 md:rotate-0" style={{ color }} />

    <div className="p-8 md:p-10 text-center" style={{ backgroundColor: color }}>
      <h5 className="font-black tracking-tight text-2xl md:text-3xl leading-tight" style={{ color: fg }}>{item.outcome}</h5>
    </div>
  </Motion.div>
);

/* Section 04 row — informational grid, deliberately different composition
   from section 03's list so the page doesn't repeat one formula. */
const WhoCard = ({ item, idx, color, Icon }) => (
  <Motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: Math.min(idx, 6) * 0.05 }}
    viewport={{ once: false, amount: 0.2 }}
    className="border-t border-border-color pt-6"
  >
    <div className="flex items-center justify-between mb-4">
      <span className="font-mono-swiss text-[10px] tabular-nums opacity-40">{String(idx + 1).padStart(2, '0')}</span>
      <Icon size={28} strokeWidth={1.5} style={{ color }} className="opacity-70" />
    </div>
    <h5 className="font-black tracking-tight text-xl md:text-2xl mb-3">{item.title}</h5>
    <p className="text-text-muted font-light leading-relaxed">{item.desc}</p>
  </Motion.div>
);

const SolutionPageTemplate = ({ lang: siteLang, CONTENT }) => {
  const lang = CONTENT[siteLang] ? siteLang : 'de';
  const c = CONTENT[lang];
  const t = TRANSLATIONS[lang];
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const heroSectionRef = useRef(null);
  const solvesRef = useRef(null);
  const idx = c.indexPrefix;

  // Category switch — every page so far has been Design, but the template
  // is shared with AI too (CONTENT sets category: 'ai'); missing/other
  // values default to Design so none of the existing Design pages need to
  // add the field. Drives the accent token, the "D"/"AI" Swiss index code,
  // and which tab Nav/Logo/BookingModal render as active — the exact same
  // values the main site itself uses for these two tabs.
  const isAI = c.category === 'ai';
  const activeTab = isAI ? 'ai' : 'design';
  const sectionCode = isAI ? 'AI' : 'D';
  // The main site's actual per-tab accent — a CSS variable, not a fixed
  // hex. --design-primary already switches itself (magenta in light,
  // yellow in dark; see index.css's :root/.dark). --ai-primary never
  // switches — it's magenta in both themes — so AI pages stay magenta-led
  // throughout instead of flipping to yellow in dark mode.
  const ACCENT = isAI ? 'var(--ai-primary)' : 'var(--design-primary)';

  // Real viewport check — same pattern the main site uses for its own
  // ServiceItem list — so the cross-link list's per-row reveal stagger
  // (section 05) actually reflects the device instead of always assuming
  // desktop.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Real theme state — these pages previously hardcoded theme="light" and
  // gave Nav a no-op setTheme, so the toggle in Nav silently did nothing.
  // Exact same logic App.jsx uses for the main site: toggle .dark on
  // document.documentElement and persist to localStorage. (App's own
  // theme effect only reruns when App's own theme state changes, which
  // never happens while a standalone SEO subpage is mounted, so there's
  // no fight over the class after the initial mount.)
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get('theme');
      if (fromUrl === 'light' || fromUrl === 'dark') return fromUrl;
      // Remember a previously chosen theme (e.g. toggled on the main site
      // or an earlier visit) instead of always resetting to light.
      const stored = localStorage.getItem('theme');
      if (stored === 'light' || stored === 'dark') return stored;
    }
    return 'light';
  });
  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Reflect theme in the URL (?theme=dark) — same convention the main
  // site's /de|en/design, /de|en/ai, /concierge etc. all use, so a shared
  // link reopens in the exact theme it was shared in. Harmless for SEO:
  // the canonical tag above always strips it back to the bare c.path
  // regardless of what's in the address bar.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('theme', theme);
      window.history.replaceState({}, '', url.toString());
    }
  }, [theme]);

  // ACCENT only ever turns yellow for Design in dark mode (AI's stays
  // magenta always), so anything filled solid with ACCENT (a panel, a
  // hover state) only needs dark ink text in that one case — same rule
  // the main site applies to its own Design-tab dark mode.
  const accentFg = (!isAI && theme === 'dark') ? '#1a1a1a' : '#ffffff';

  // Same swap the main site's real hero does per tab (isHeroMagenta =
  // isHeroAI || (activeTab === 'design' && theme === 'light')) — AI is
  // always magenta, Design flips to yellow in dark mode. Drives both the
  // hero and the final CTA, which are the same kind of fixed Swiss-poster
  // block, just top and bottom of the page.
  const isPosterMagenta = isAI || theme === 'light';
  const posterBg = isPosterMagenta ? MAGENTA : YELLOW;
  const posterInk = isPosterMagenta ? '#ffffff' : 'var(--cre8-ink)';
  const posterAccent = isPosterMagenta ? YELLOW : MAGENTA;
  const posterAccentText = isPosterMagenta ? 'var(--cre8-ink)' : '#ffffff';
  const posterMuted = isPosterMagenta ? 'rgba(255,255,255,0.8)' : 'rgba(13,13,13,0.7)';
  const posterBorder = isPosterMagenta ? 'rgba(255,255,255,0.25)' : 'rgba(13,13,13,0.2)';

  const openWizard = (source) => {
    trackEvent(`${c.eventPrefix}_cta_click`, { source, lang });
    setIsBookingOpen(true);
  };

  const goToTab = (tab) => {
    // Real, lang-prefixed paths (/de|en/design, /de|en/ai, /de|en/cre8guide,
    // /de|en/concierge) — same convention this page itself is already
    // reachable at.
    window.location.href = `/${lang}/${tab}`;
  };
  const goToLegal = () => { window.location.href = `/${lang}/impressum`; };
  const switchLang = (l) => { window.location.href = l === 'de' ? CONTENT.de.path : CONTENT.en.path; };

  // --- SEO head management: title, description, canonical, hreflang, JSON-LD ---
  useEffect(() => {
    document.title = c.title;
    document.documentElement.lang = c.htmlLang;
    const setMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', 'content', c.description);
    setMeta('meta[property="og:title"]', 'content', c.title);
    setMeta('meta[property="og:description"]', 'content', c.description);
    // Twitter/X reads its own namespace and falls back to the og: tags only
    // when its own are absent — index.html ships static ones, so they were
    // never absent and every card carried the generic homepage title.
    setMeta('meta[name="twitter:title"]', 'content', c.title);
    setMeta('meta[name="twitter:description"]', 'content', c.description);
    setMeta('meta[property="og:url"]', 'content', ORIGIN + c.path);
    setMeta('meta[property="og:locale"]', 'content', lang === 'de' ? 'de_DE' : 'en_US');
    // Per-page social card — a real screenshot of this page's own hero
    // (same generator used across every solution page), not the sitewide
    // default. Only overrides once a page actually has one; others keep
    // falling back to index.html's generic Cre8Now_Social.jpg static tag.
    if (c.ogImage) {
      const ogImageUrl = ORIGIN + c.ogImage;
      const ogImageAlt = `${c.serviceName} — CRE8NOW`;
      setMeta('meta[property="og:image"]', 'content', ogImageUrl);
      setMeta('meta[property="og:image:alt"]', 'content', ogImageAlt);
      setMeta('meta[name="twitter:image"]', 'content', ogImageUrl);
      setMeta('meta[name="twitter:image:alt"]', 'content', ogImageAlt);
    }

    const canonicalTag = document.getElementById('canonical-tag');
    if (canonicalTag) canonicalTag.setAttribute('href', ORIGIN + c.path);
    const hreflangDe = document.querySelector('link[rel="alternate"][hreflang="de"]');
    const hreflangEn = document.querySelector('link[rel="alternate"][hreflang="en"]');
    const hreflangDefault = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
    if (hreflangDe) hreflangDe.setAttribute('href', ORIGIN + CONTENT.de.path);
    if (hreflangEn) hreflangEn.setAttribute('href', ORIGIN + CONTENT.en.path);
    if (hreflangDefault) hreflangDefault.setAttribute('href', ORIGIN + CONTENT.de.path);

    const ld = [
      { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'CRE8NOW', item: ORIGIN + '/' },
        { '@type': 'ListItem', position: 2, name: isAI ? 'AI' : 'Design', item: ORIGIN + '/' },
        { '@type': 'ListItem', position: 3, name: c.serviceName, item: ORIGIN + c.path },
      ] },
      { '@context': 'https://schema.org', '@type': 'Service', '@id': ORIGIN + c.path + '#service', name: c.serviceName, serviceType: c.serviceName, provider: { '@id': ORIGIN + '/#organization' }, areaServed: 'Worldwide', availableLanguage: ['German', 'English'], url: ORIGIN + c.path, description: c.description },
      { '@context': 'https://schema.org', '@type': 'Person', name: 'Pascal Hoffmann', jobTitle: lang === 'de' ? 'Gründer, CRE8NOW' : 'Founder, CRE8NOW', worksFor: { '@id': ORIGIN + '/#organization' }, knowsAbout: ['UX Design', 'UI Design', 'Prototyping', 'Digital Product Design'] },
    ];
    const scripts = ld.map((obj) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-page', c.slug);
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });
    return () => scripts.forEach((s) => s.remove());
  }, [c, lang]);

  return (
    <Motion.div
      key="main"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="bg-bg-color text-text-color min-h-screen overflow-x-clip"
    >
      {/* Sitewide print-grain texture, same as the main site — viewport-
          locked so it stays cheap regardless of page length, sits above
          content but below chrome. */}
      <GrainOverlay fixed opacity={0.03} className="z-40" />
      <Nav
        activeTab={activeTab}
        setActiveTab={goToTab}
        setView={goToLegal}
        color={ACCENT}
        lang={lang}
        setLang={switchLang}
        t={t}
        theme={theme}
        setTheme={setTheme}
        onOpenBookingModal={() => openWizard('nav')}
      />

      {/* ===== 01 — INTRO ===== */}
      <section ref={heroSectionRef} className="relative min-h-[100dvh] w-full flex items-center justify-center gpu-accelerated overflow-hidden" style={{ backgroundColor: posterBg }}>
        {/* The actual "rasterized" hero texture the main site uses — a
            12-column vertical line grid, not grain/noise — colored with
            posterInk so it reads on both poster colors.

            Faded in rather than painted instantly: it used to snap on while the
            headline was still animating, so the backdrop arrived ahead of the
            content standing on it. Slightly longer and flatter than the text
            entrance so it reads as the surface settling, not a second element
            competing for attention. */}
        <Motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4, ease: 'easeOut' }}
          className="absolute inset-0 pointer-events-none"
        >
          <GridOverlay columns={12} opacity={isPosterMagenta ? 0.16 : 0.08} color={posterInk} />
        </Motion.div>
        <div className="hidden md:block absolute inset-0 z-10 pointer-events-none" style={{ color: posterInk }}>
          <CropMarks insetTop={8} insetX={8} showBottom={false} color={posterAccent} />
          <div className="flex absolute top-24 left-6 items-center gap-4">
            <RegistrationMark size={16} style={{ color: posterAccent }} />
            <IndexTag code={sectionCode} num={`${idx}-001`} color={posterAccent} />
          </div>
          <div className="flex absolute top-24 right-6 items-center gap-4">
            <LiveCoordTag />
            <RegistrationMark size={16} style={{ color: posterAccent }} />
          </div>
          {/* Single-line Swiss breadcrumb, sitting just clear of the brand
              marquee bar anchored to the hero's bottom edge. Styled like
              FlowChain (mono, uppercase, letter-spaced, last segment lit)
              but one line only, filled with the page trail. */}
          <div className="absolute bottom-20 md:bottom-24 left-6 flex items-center flex-wrap gap-x-3 gap-y-1 font-mono-swiss text-[9px] md:text-[10px] font-semibold uppercase tracking-[0.25em]">
            {c.breadcrumb.map((part, i) => (
              <React.Fragment key={part}>
                <span style={{ color: i === c.breadcrumb.length - 1 ? posterAccent : undefined }}>{part}</span>
                {i < c.breadcrumb.length - 1 && <span className="opacity-50">/</span>}
              </React.Fragment>
            ))}
          </div>
          <VerticalLabel text="CRE8NOW / SYSTEM" side="left" offset={24} />
          <VerticalLabel text={`MODE / ${idx}`} side="right" offset={24} />
        </div>

        {/* Hero content reveals in on load — same staggered scale/opacity
            entrance the main site's hero uses, not a static paint-in. */}
        <div className="relative z-20 text-center px-[8vw]" style={{ color: posterInk }}>
          <Motion.h4
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.83, 0, 0.17, 1] }}
            className="hero-h4 block mb-1 leading-[0.95] uppercase tracking-tight [text-shadow:none]"
          >
            {c.intro.headline[0]}
          </Motion.h4>
          <Motion.h1
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8, ease: [0.83, 0, 0.17, 1] }}
            className="font-black leading-[0.85] tracking-tight uppercase w-full mt-4"
          >
            <span className="inline-block audiowide-regular font-normal text-[1.08em] md:text-[1.4em]" style={{ color: posterAccent }}>
              {c.intro.headline[1]}
            </span>
          </Motion.h1>
          <Motion.p
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.25, duration: 0.8, ease: [0.83, 0, 0.17, 1] }}
            className="max-w-[1100px] w-[90vw] hero-copy mx-auto mt-3 md:-mt-4 text-lg md:text-xl font-light leading-relaxed opacity-95"
          >
            {c.intro.copy}
          </Motion.p>
          <Motion.div
            initial={{ opacity: 0, scale: 1.2 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.6, ease: [0.83, 0, 0.17, 1] }}
            className="flex items-center justify-center mt-10"
          >
            <PrimaryButton onClick={() => openWizard('hero')} bgColor={posterInk} textColor={posterBg} hoverFill={posterAccent} hoverTextColor={posterAccentText}>
              {c.intro.ctaPrimary}
            </PrimaryButton>
          </Motion.div>
        </div>

        {/* Real, approved brand roster — same white "darkmode" logo variants
            used sitewide, in a marquee anchored to the hero's bottom edge
            (matching the main hero's outcomes-marquee pattern) instead of a
            separate Brands section. Per-item border-r like the main hero's
            own marquee, each bar filled with the poster's own bg color. */}
        {/* Last of the hero sequence: the roster settles in under the copy
            after the CTA has landed, instead of being there from frame one
            while everything above it was still arriving. */}
        <Motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.7, ease: [0.83, 0, 0.17, 1] }}
          className="absolute bottom-0 left-0 z-20 w-full overflow-hidden border-t"
          style={{ borderColor: posterBorder }}
        >
          <div className="flex will-change-transform animate-infinite-scroll-fast">
            {[0, 1].map((set) => (
              <div key={set} className="flex items-center flex-shrink-0" aria-hidden={set === 1 ? true : undefined}>
                {BRAND_LOGOS.map((logo, i) => (
                  <div key={i} className="flex items-center gap-4 pl-8 pr-8 md:pl-10 md:pr-10 py-2 md:py-3 flex-shrink-0 border-r" style={{ borderColor: posterBorder, backgroundColor: posterBg }}>
                    {/* The logo assets are one fixed variant with a chrome-
                        style white-to-grey gradient baked into the pixels
                        (no flat/dark counterpart exists), which reads as
                        dirty grey smudging now that each bar has an opaque
                        poster-color fill behind it. brightness(0) first
                        flattens every opaque pixel to solid black
                        regardless of its original shade — erasing the
                        gradient/"dark ones" entirely — then invert(1) flips
                        that to solid white for the magenta poster; on the
                        yellow poster brightness(0) alone already gives the
                        solid-black silhouette we want. */}
                    {/* No loading="lazy" here: these images sit inside a
                        continuously transformed, looping marquee, not a
                        real scroll position — the browser's viewport-based
                        lazy-load heuristic reads that unreliably (worst on
                        mobile Safari), so logos intermittently never
                        resolve as "in view" and drop out mid-scroll. The
                        roster is small and always eventually visible, so
                        loading it eagerly is the correct trade-off. */}
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      width={200}
                      height={80}
                      className="max-h-12 md:max-h-16 w-auto object-contain opacity-95"
                      style={{ filter: isPosterMagenta ? 'brightness(0) invert(1)' : 'brightness(0)' }}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Motion.div>
      </section>

      {/* ===== STATS STRIP — right under the hero, centered ===== */}
      <StatsStrip items={c.stats.map((s) => ({ value: s.n, label: s.l }))} color={ACCENT} />

      {/* ===== 02 — ABOUT & SOLVES: what it is, why it matters, what it
          solves (as a before/after per problem, not a separate section) ===== */}
      <section id={`${c.slug}-about`} ref={solvesRef} className="relative w-full py-24 md:py-32 bg-bg-color">
        <SectionFrame code={sectionCode} num={`${idx}-002`} color={ACCENT} verticalLabelRight="MODE / ABOUT" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10"><SectionCounter current={2} total={TOTAL_SECTIONS} color={ACCENT} /></div>
        <div className="w-full px-[8vw]">
          <SectionHeading label={c.about.label} title={c.about.title} highlight={c.about.highlight} description={c.about.description} color={ACCENT} theme={theme} centered />
          {c.solves.description && (
            // Real <h4> using the site's actual h4 style as-is (no font
            // overrides) — the same treatment used elsewhere, not a
            // one-off subheadline style invented for this page.
            <h4 className="w-[85%] max-w-[900px] mx-auto text-center -mt-4 mb-24 md:mb-28" style={{ color: ACCENT }}>
              {c.solves.description}
            </h4>
          )}
          <div className="space-y-12 md:space-y-8">
            {c.solves.items.map((item, i) => (
              <SolveRow key={item.problem} item={item} idx={i} color={ACCENT} fg={accentFg} />
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-2 mt-10 pt-8 border-t border-border-color">
            {c.solves.capabilities.map((cap) => (
              <span key={cap} className="text-[9px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 border border-border-color text-text-muted opacity-70">{cap}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 03 — WHO CRE8NOW SOLVES IT FOR ===== */}
      <section className="relative w-full py-24 md:py-32 bg-bg-color border-t border-border-color">
        <SectionFrame code={sectionCode} num={`${idx}-003`} color={ACCENT} verticalLabelRight="MODE / AUDIENCE" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10"><SectionCounter current={3} total={TOTAL_SECTIONS} color={ACCENT} /></div>
        <div className="w-full px-[8vw]">
          <SectionHeading label={c.who.label} title={c.who.title} highlight={c.who.highlight} description={c.who.description} color={ACCENT} theme={theme} centered />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12">
            {c.who.groups.map((item, i) => (
              <WhoCard key={item.title} item={item} idx={i} color={ACCENT} Icon={CAPABILITY_ICONS[i % CAPABILITY_ICONS.length]} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== 04 — FAQ ===== */}
      <section className="relative w-full py-24 md:py-32 bg-bg-color border-t border-border-color">
        <SectionFrame code={sectionCode} num={`${idx}-004`} color={ACCENT} verticalLabelRight="MODE / FAQ" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10"><SectionCounter current={4} total={TOTAL_SECTIONS} color={ACCENT} /></div>
        <div className="w-full px-[8vw]">
          <SectionHeading label={c.faq.label} title={c.faq.title} highlight={c.faq.highlight} color={ACCENT} theme={theme} centered />
          <div>{c.faq.items.map((item, i) => <FaqRow key={item.q} item={item} idx={i} color={ACCENT} />)}</div>
        </div>
      </section>

      {/* ===== 05 — MORE SOLUTIONS (cross-links, Design or AI) ===== */}
      <section className="relative w-full py-24 md:py-32 bg-bg-color border-t border-border-color">
        <SectionFrame code={sectionCode} num={`${idx}-005`} color={ACCENT} verticalLabelRight="MODE / SOLUTIONS" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10"><SectionCounter current={5} total={TOTAL_SECTIONS} color={ACCENT} /></div>
        <div className="w-full px-[8vw]">
          <SectionHeading label={c.more.label} title={c.more.title} highlight={c.more.highlight} description={c.more.description} color={ACCENT} theme={theme} centered />
        </div>
        {/* Same expertise-list row component and style as the main site's
            Design services grid — real anchors where a dedicated page
            exists (the sibling UX/UI page), plain informational rows for
            everything else, exactly like the main list treats them. Unlike
            the main site's full-bleed version, this list sits inside the
            same px-[8vw] margins every other section on this page uses. */}
        <div className="flex flex-col border-t border-border-color w-full px-[8vw]">
          {c.more.items.map((item, i) => (
            <ServiceItem
              key={item.title}
              service={item}
              i={i}
              total={c.more.items.length}
              color={ACCENT}
              isMobile={isMobile}
              currentIcons={CAPABILITY_ICONS}
              theme={theme}
              hoverTextColor={accentFg}
            />
          ))}
        </div>
      </section>

      {/* ===== 06 — CTA / ERSTGESPRÄCH ===== */}
      <section
        className="relative w-full py-24 md:py-32"
        style={{ backgroundColor: posterBg, color: posterInk, '--color-text-color': posterInk, '--color-text-muted': posterMuted, '--color-border-color': posterBorder }}
      >
        <GridOverlay columns={12} opacity={isPosterMagenta ? 0.16 : 0.08} color={posterInk} />
        <SectionFrame code={sectionCode} num={`${idx}-006`} color={posterAccent} verticalLabelRight="MODE / CTA" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10"><SectionCounter current={6} total={TOTAL_SECTIONS} color={posterAccent} /></div>
        <div className="w-full px-[8vw]">
          <SectionHeading label={c.cta.micro} title={c.cta.title} highlight={c.cta.highlight} description={c.cta.description} color={posterAccent} theme={isPosterMagenta ? 'dark' : 'light'} centered />
          <div className="flex items-center justify-center -mt-10">
            <PrimaryButton onClick={() => openWizard('final')} bgColor={posterInk} textColor={posterBg} hoverFill={posterAccent} hoverTextColor={posterAccentText}>
              {c.cta.ctaPrimary}
            </PrimaryButton>
          </div>
        </div>
      </section>

      {/* ===== FOOTER — same as the main site footer ===== */}
      {/* The main site's footer, link columns and all. Passing no
          setActiveTab/setView means every entry navigates to its real URL —
          which is what should happen from a standalone solution page anyway.
          It also gives these 19 pages a full internal link graph: previously
          they offered three links and were a near-dead end for crawlers. */}
      <SiteFooter theme={theme} activeTab={activeTab} lang={lang} color={ACCENT} t={t} />

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} lang={lang} color={ACCENT} activeTab={activeTab} initialReasons={[c.serviceName]} />
    </Motion.div>
  );
};

export default SolutionPageTemplate;
